(function () {
  const SELECTOR = "[data-rental-search]";
  const WEEKDAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

  function pad(value) {
    return String(value).padStart(2, "0");
  }

  function toDateKey(date) {
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  }

  function formatDisplayDate(dateKey) {
    const [year, month, day] = dateKey.split("-").map(Number);
    return new Date(year, month - 1, day).toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
    });
  }

  function buildCalendarWeeks(monthDate) {
    const firstOfMonth = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
    const startOffset = firstOfMonth.getDay();
    const gridStart = new Date(firstOfMonth);
    gridStart.setDate(gridStart.getDate() - startOffset);

    const days = [];
    for (let i = 0; i < 42; i += 1) {
      const day = new Date(gridStart);
      day.setDate(gridStart.getDate() + i);
      days.push(day);
    }
    return days;
  }

  function initDatePicker(root) {
    const wrap = root.querySelector("[data-rental-search-daterange]");
    const trigger = root.querySelector("[data-rental-search-date-trigger]");
    const label = root.querySelector("[data-rental-search-date-label]");
    const dateInput = root.querySelector("[data-rental-search-date]");
    const popover = root.querySelector("[data-rental-search-popover]");
    const monthLabel = root.querySelector("[data-rental-search-month]");
    const hint = root.querySelector("[data-rental-search-hint]");
    const grid = root.querySelector("[data-rental-search-grid]");
    const prevButton = root.querySelector("[data-rental-search-prev]");
    const nextButton = root.querySelector("[data-rental-search-next]");

    let visibleMonth = new Date();

    // Move the popover to <body> and render it position: fixed. Themes stack
    // sections in the normal document flow, and a deeply nested z-index can
    // still lose to a later, unrelated section (that's what was happening -
    // the newsletter block below was painting over the calendar). Detaching
    // it from the widget's DOM position sidesteps that entirely.
    //
    // Custom properties only cascade through the DOM tree, so moving the
    // popover out from under `root` would otherwise lose its colors - copy
    // them across as explicit inline values first.
    const rootStyle = getComputedStyle(root);
    [
      "--dr-search-border",
      "--dr-search-text",
      "--dr-search-muted",
      "--dr-search-accent",
      "--dr-search-button-text",
    ].forEach((property) => {
      const value = rootStyle.getPropertyValue(property);
      if (value) popover.style.setProperty(property, value.trim());
    });
    popover.style.fontFamily = rootStyle.fontFamily;
    document.body.appendChild(popover);

    function updateLabel() {
      label.textContent = dateInput.value ? formatDisplayDate(dateInput.value) : "Choose a date";
    }

    function positionPopover() {
      const rect = trigger.getBoundingClientRect();
      const popoverWidth = popover.offsetWidth || 300;
      const popoverHeight = popover.offsetHeight || 360;
      const left = Math.min(rect.left, window.innerWidth - popoverWidth - 12);

      // Prefer opening below the trigger, but flip above it if there isn't
      // enough room in the viewport - otherwise the calendar can render
      // partly or fully below the fold, where days can't be hovered/clicked.
      const fitsBelow = rect.bottom + 8 + popoverHeight <= window.innerHeight;
      const top = fitsBelow ? rect.bottom + 8 : Math.max(12, rect.top - popoverHeight - 8);

      popover.style.top = `${top}px`;
      popover.style.left = `${Math.max(12, left)}px`;
    }

    function closePopover() {
      popover.hidden = true;
      window.removeEventListener("scroll", positionPopover, true);
      window.removeEventListener("resize", positionPopover);
    }

    function openPopover() {
      visibleMonth = dateInput.value ? new Date(`${dateInput.value}T00:00:00`) : new Date();
      popover.hidden = false;
      render();
      positionPopover();
      window.addEventListener("scroll", positionPopover, true);
      window.addEventListener("resize", positionPopover);
    }

    function handleDayClick(dateKey) {
      dateInput.value = dateKey;
      updateLabel();
      closePopover();
    }

    function render() {
      monthLabel.textContent = visibleMonth.toLocaleDateString(undefined, {
        month: "long",
        year: "numeric",
      });
      hint.textContent = "Pick a date";

      const todayKey = toDateKey(new Date());

      const cells = WEEKDAY_LABELS.map(
        (dayLabel) => `<span class="dr-search__weekday">${dayLabel}</span>`
      );

      buildCalendarWeeks(visibleMonth).forEach((day) => {
        const dayKey = toDateKey(day);
        const inCurrentMonth = day.getMonth() === visibleMonth.getMonth();
        const isSelected = dayKey === dateInput.value;

        const classes = ["dr-search__day"];
        if (!inCurrentMonth) classes.push("dr-search__day--muted");
        if (dayKey === todayKey) classes.push("dr-search__day--today");
        if (isSelected) classes.push("dr-search__day--start");

        cells.push(
          `<button type="button" class="${classes.join(" ")}" data-date="${dayKey}">${day.getDate()}</button>`
        );
      });

      grid.innerHTML = cells.join("");

      grid.querySelectorAll("[data-date]").forEach((button) => {
        button.addEventListener("click", () => handleDayClick(button.dataset.date));
      });
    }

    trigger.addEventListener("click", () => {
      if (popover.hidden) openPopover();
      else closePopover();
    });

    prevButton.addEventListener("click", () => {
      visibleMonth = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() - 1, 1);
      render();
    });

    nextButton.addEventListener("click", () => {
      visibleMonth = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + 1, 1);
      render();
    });

    document.addEventListener("click", (event) => {
      if (!wrap.contains(event.target) && !popover.contains(event.target)) closePopover();
    });
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape") closePopover();
    });
  }

  function buildMetaUrl(root) {
    const endpoint = root.dataset.metaUrl || "/apps/dress-rental/search";
    const url = new URL(endpoint, window.location.origin);
    url.searchParams.set("shop", root.dataset.shop || "");
    return url.toString();
  }

  function populateSelectOptions(select, options, valueKey, labelKey) {
    // A preset value (e.g. on the search-results page) can add a placeholder
    // option using the raw value as its label before this data arrives - once
    // the real label is known, overwrite that placeholder's text rather than
    // skipping it, so shoppers see "Express Post" instead of "post".
    const existingByValue = new Map(Array.from(select.options).map((option) => [option.value, option]));
    options.forEach((option) => {
      const value = String(option[valueKey]);
      const label = option[labelKey];
      const existingOption = existingByValue.get(value);
      if (existingOption) {
        existingOption.textContent = label;
        return;
      }
      const el = document.createElement("option");
      el.value = value;
      el.textContent = label;
      select.appendChild(el);
    });
  }

  async function loadFilterOptions(root) {
    const deliverySelect = root.querySelector("[data-rental-search-delivery]");
    const durationSelect = root.querySelector("[data-rental-search-duration]");
    const sizeSelect = root.querySelector("[data-rental-search-size]");

    try {
      const response = await fetch(buildMetaUrl(root), {
        credentials: "same-origin",
        headers: { Accept: "application/json" },
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.enabled === false) {
        root.hidden = true;
        return;
      }

      if (Array.isArray(data.deliveryMethods)) {
        populateSelectOptions(deliverySelect, data.deliveryMethods, "id", "label");
      }
      if (Array.isArray(data.durations)) {
        const durationOptions = data.durations.map((duration) => ({
          value: duration,
          label: `${duration} days`,
        }));
        populateSelectOptions(durationSelect, durationOptions, "value", "label");
      }
      if (Array.isArray(data.sizes)) {
        const sizeOptions = data.sizes.map((size) => ({ value: size, label: size }));
        populateSelectOptions(sizeSelect, sizeOptions, "value", "label");
      }
    } catch (error) {
      console.error("[Rental Search] Failed to load filter options.", error);
    }
  }

  function initWidget(root) {
    const form = root.querySelector("[data-rental-search-form]");
    const dateInput = root.querySelector("[data-rental-search-date]");
    const durationSelect = root.querySelector("[data-rental-search-duration]");
    const messageEl = root.querySelector("[data-rental-search-message]");
    const submitButton = root.querySelector("[data-rental-search-submit]");

    if (!form || !dateInput || !messageEl) return;

    initDatePicker(root);
    loadFilterOptions(root);

    form.addEventListener("submit", (event) => {
      if (!dateInput.value || !durationSelect.value) {
        event.preventDefault();
        messageEl.hidden = false;
        messageEl.textContent = "Please choose a date and duration.";
        return;
      }

      messageEl.hidden = true;
      submitButton.disabled = true;
      // Let the browser submit the form as a normal GET navigation to the
      // search-results page - no need to prevent default here.
    });
  }

  function init() {
    document.querySelectorAll(SELECTOR).forEach(initWidget);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
